import {runAsync} from "@prosay/common"
import {
    AudioPlayer,
    AudioRecorder,
    AudioRecorderState,
    TextInput,
} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import {defaultDataIdFromObject} from "apollo-cache-inmemory"
import * as ImagePicker from "expo-image-picker"
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {
    AsyncStorage,
    StyleSheet,
    // eslint-disable-next-line sort-imports
    TextInput as NativeTextInput,
    View,
} from "react-native"
import {Button, Divider, IconButton, Menu} from "react-native-paper"
import {sendMessageMutationResponse} from "../data"
import {
    GetMessagesDocument,
    GetMessagesQuery,
    GetMessagesQueryVariables,
    LastMessageFragment,
    LastMessageFragmentDoc,
    useSendMessageMutation,
} from "../graphql"
import {dataURIToBlob, readQuery} from "../util"
import {Field, Form} from "./Form"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    container: {
        // marginBottom: 13, // on web, this needs to be 13.
        borderTopColor: "rgba(0, 0, 0, 0.1)",
        borderTopWidth: 1,
        // height: 45,
        flexDirection: "row",

        left: 0,
        right: 0,
    },
    input: {
        // borderColor: colors.border,
        // borderTopWidth: 1,
    },
    inputContainer: {
        flex: 1,
    },
    sendButton: {
        borderBottomRightRadius: 0,
        borderTopRightRadius: 0,
    },
})

const getInputBarMessage = (
    state: AudioRecorderState | undefined,
    image: Blob | undefined,
) => {
    const getInputBarMessageRecorder = (state: AudioRecorderState) => {
        switch (state.state) {
            case "recording":
                return translate(
                    "ui.components.messageInputBar.recordingLabel",
                    {
                        duration: `${Math.floor(state.duration)}`,
                    },
                )
            case "finished":
                return translate(
                    "ui.components.messageInputBar.recordingLabel",
                    {
                        duration: `${Math.ceil(state.duration / 1000)}`,
                    },
                )
            default:
                return undefined
        }
    }

    const getInputBarMessageImage = (image: Blob | undefined) =>
        image
            ? translate("ui.components.messageInputBar.imageAttachedLabel")
            : undefined

    return (
        getInputBarMessageRecorder(state ?? {state: "initial"}) ??
        getInputBarMessageImage(image)
    )
}

export interface ImageSelectionButtonProps {
    disabled?: boolean
    onImageUpdate: (blob: Blob | undefined) => void
}
export const ImageSelectionButton: React.FC<ImageSelectionButtonProps> = ({
    disabled = false,
    onImageUpdate,
}) => {
    const onPress = useCallback(
        () =>
            runAsync(async () => {
                const result = await ImagePicker.launchImageLibraryAsync({
                    allowsEditing: true,
                })
                if (result.cancelled) {
                    logger.debug`Received the following result from image picker: ${{
                        ...result,
                    }}`
                    return
                }
                logger.debug`Received the following result from image picker: ${{
                    ...result,
                    uri: result.uri.substr(0, 50),
                }}`

                onImageUpdate(dataURIToBlob(result.uri))
            }),
        [onImageUpdate],
    )

    return (
        <Button>
            <IconButton icon="file-image" {...{disabled, onPress}} />
        </Button>
    )
}

export interface DraftHandlerProps {
    conversationId: string
    onChangeText: (value: string) => void
    value: string | null | undefined
}
export const DraftHandler: React.FC<DraftHandlerProps> = ({
    conversationId,
    onChangeText,
    value,
}) => {
    const id = useMemo(() => `Messages:draft:${conversationId}`, [
        conversationId,
    ])

    useEffect(() => {
        runAsync(async () => {
            const value = await AsyncStorage.getItem(id)
            if (!value) {
                return
            }
            onChangeText(value)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    useEffect(() => {
        runAsync(async () => {
            if (!value) {
                await AsyncStorage.removeItem(id)
            } else {
                await AsyncStorage.setItem(id, value)
            }
        })
    }, [id, value])

    return null
}

export interface MessagesInputBarProps {
    conversationId: string
    disableImageButton?: boolean
    disableRecordingButton?: boolean
    sendSnackMessage: (message: string) => void
}
// TODO: Voice does not work on Safari on the iPhone
export const MessagesInputBar: React.FC<MessagesInputBarProps> = ({
    conversationId,
    disableImageButton = false,
    disableRecordingButton = false,
    sendSnackMessage,
}) => {
    const [image, setImage] = useState<Blob>()
    const [recording, setRecording] = useState<AudioRecorderState>()

    const textInputRef = useRef<NativeTextInput>(null)
    const refocusTextBox = useCallback(
        () => textInputRef.current?.focus?.(),
        [],
    )

    const mutation = useSendMessageMutation({
        onCompleted: () => {
            setImage(undefined)
            setRecording(undefined)
            refocusTextBox()
        },
        optimisticResponse: sendMessageMutationResponse,
        update: (cache, {data, errors}) => {
            if (errors || !data?.sendMessage) {
                return
            }

            const existing = readQuery<
                GetMessagesQuery,
                GetMessagesQueryVariables
            >(cache, {
                query: GetMessagesDocument,
                variables: {conversationId},
            })
            cache.writeQuery<GetMessagesQuery, GetMessagesQueryVariables>({
                data: {
                    __typename: "Query",
                    getMessages: [
                        data.sendMessage,
                        ...(existing?.getMessages ?? []),
                    ],
                },
                query: GetMessagesDocument,
                variables: {conversationId},
            })

            const id = defaultDataIdFromObject({
                __typename: "Conversation",
                id: conversationId,
            })
            if (!id) {
                return
            }

            cache.writeFragment<LastMessageFragment>({
                data: {
                    __typename: "Conversation",
                    lastMessage: data.sendMessage,
                    lastMessageUpdatedAt: data.sendMessage.createdAt,
                },
                fragment: LastMessageFragmentDoc,
                fragmentName: "LastMessage",
                id,
            })
        },
        variables: {
            conversationId,
            imageContent: image,
            voiceContent:
                recording?.state === "finished" ? recording.blob : undefined,
        },
    })

    const messageType: "audio" | "image" | "text" = useMemo(() => {
        if (["finished", "recording"].includes(recording?.state ?? "")) {
            return "audio"
        }

        if (image) {
            return "image"
        }

        return "text"
    }, [image, recording])

    const inputTitle = useMemo(() => getInputBarMessage(recording, image), [
        image,
        recording,
    ])

    const [assetMenuVisible, setAssetMenuVisible] = useState(false)
    const onMenuDismiss = useCallback(() => setAssetMenuVisible(false), [
        setAssetMenuVisible,
    ])
    const onMenuOpen = useCallback(() => setAssetMenuVisible(true), [
        setAssetMenuVisible,
    ])

    return (
        <Form
            mutation={mutation}
            initialValues={{
                conversationId,
                textContent: "",
            }}
            autoReset>
            {({isSubmitting, isValid, submitForm, values}) => (
                <View style={styles.container}>
                    <Menu
                        visible={
                            assetMenuVisible || recording?.state === "recording"
                        }
                        onDismiss={onMenuDismiss}
                        anchor={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <Button onPress={onMenuOpen}>
                                <IconButton icon="plus" />
                            </Button>
                        }>
                        {recording?.state === "finished" ? (
                            <AudioPlayer audio={recording.blob} />
                        ) : (
                            <AudioRecorder
                                sendSnackMessage={sendSnackMessage}
                                disabled={disableRecordingButton || !!image}
                                setState={setRecording}
                            />
                        )}
                        <Divider />
                        <ImageSelectionButton
                            onImageUpdate={setImage}
                            disabled={
                                disableImageButton ||
                                (recording?.state ?? "initial") !== "initial"
                            }
                        />
                    </Menu>

                    {recording?.state === "finished" && (
                        <Button onPress={() => setRecording(undefined)}>
                            <IconButton icon="close" />
                        </Button>
                    )}

                    {image && recording?.state !== "finished" && (
                        <Button onPress={() => setImage(undefined)}>
                            <IconButton icon="close" />
                        </Button>
                    )}

                    <Field mutation={mutation} name="textContent">
                        {({field: {error, onBlur, onChangeText, value}}) => (
                            <>
                                <DraftHandler
                                    {...{conversationId, onChangeText, value}}
                                />
                                <TextInput
                                    ref={textInputRef}
                                    mode="outlined"
                                    containerStyle={styles.inputContainer}
                                    style={styles.input}
                                    blurOnSubmit={false}
                                    onSubmitEditing={submitForm}
                                    placeholder={
                                        inputTitle ??
                                        translate(
                                            "ui.components.messageInputBar.textPlaceholder",
                                        )
                                    }
                                    disabled={!!inputTitle}
                                    {...{
                                        error,
                                        onBlur,
                                        onChangeText,
                                        value: value ?? "",
                                    }}
                                />
                            </>
                        )}
                    </Field>

                    <Button
                        style={styles.sendButton}
                        disabled={
                            !isValid ||
                            (messageType === "text" && !values.textContent) ||
                            isSubmitting
                        }
                        onPress={submitForm}>
                        <IconButton icon="send" />
                    </Button>
                </View>
            )}
        </Form>
    )
}
